import { generateRequest } from './request.js';
import { AveRequestEvents } from '../events.js';
/**
 * Set Camera Orbit into the requested State.
 *
 * Limitations:
 * - Calls with the same command are idempotent. So if you pause a paused orbit, it does not change the orbit state and treats the request as a success.
 * - Not available while an Environment is loading.
 * - Not available when another car is loading.
 *
 * Notes:
 * - If the command is a pause, the current camera orbit is set to pause state.
 * - If the command is a reset:
 *   - The current camera orbit is reset to the first frame.
 *   - The current camera orbit is set to pause state.
 *   - The transition is visualized to the user by a screen fade-in.
 * - The camera will return to the first frame of the currently selected camera orbit via a screen fade-in, and then pause.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEModifyCameraOrbit",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {
 *     "command" : <String>
 *   }
 * }]
 * ```
 */
export function generateModifyCameraOrbitRequest({ command, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVEModifyCameraOrbit,
        data: { command }
    });
}
/**
 * Message to start camera orbit mode. The AVE should display camera flights. The car itself is unaffected by this.
 *
 * Limitations:
 * - Not available while an Environment is loading.
 * - Not available when another car is loading.
 * - Not available when rendering an image sequence.
 * - Not available when rendering an image job.
 * - Not available when a driving scene is active.
 * - Not available when an experience is active.
 *
 * Notes:
 * - If there exists a matching paused camera orbit, it is resumed without fade-in.
 * - If there exists a matching playing camera orbit, AVE continues playing the current camera orbit and treats the request as a success.
 * - In all other cases, the camera orbit is started with a fade-in.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEStartOrResumeCameraOrbit",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {
 *     "orbit" : <String>
 *   }
 * }]
 * ```
 */
export function generateStartOrResumeCameraOrbitRequest({ orbit, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEStartOrResumeCameraOrbit, data: { orbit } });
}
/**
 * Stop currently active camera orbit.
 *
 * Limitations:
 * - Does not return an update if no camera orbit is active when called.
 * - Not available while an Environment is loading.
 * - Not available when another car is loading.
 *
 * Notes:
 * - The camera returns to the previously selected camera before the orbit was started.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEStopCameraOrbit",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateStopCameraOrbitRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEStopCameraOrbit });
}
/**
 * List all available Camera Orbits in the current scene.
 *
 * Limitations:
 * - Returns an empty list, if no camera orbits are available when called
 * - Not available while an Environment is loading
 * - Not available when another car is loading
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEListCameraOrbits",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateListCameraOrbitsRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEListCameraOrbits });
}
//# sourceMappingURL=camera-orbit.js.map